import React, { useEffect, useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Button } from './button';
import { useUser } from './UserContext';
import {
  useAsociados, useClientContracts, useClients, useComisionLedger,
} from '../contexts/DataContext';
import { logBitacora } from '../utils/bitacora';
import {
  rowsParaEditor, primaNetaDesdeRows, otrosIngresosDesde, errorRows,
  candidatosReparto, describirReparto, cambiosReparto,
} from '../utils/reparto';
import { RepartoEditor } from './RepartoEditor';
import { RepartoRetroactivoDialog } from './RepartoRetroactivoDialog';
import { PropuestaRepartoPendiente, propuestaPara } from './PropuestaRepartoPendiente';
import { proponerReparto } from '../api/comisiones';

/**
 * ClienteDetails → Asociados. Owner-only, module-gated (orgs.asociadosEnabled).
 *
 * The cliente's default reparto of the comisión sobre prima neta. Saving
 * writes clientes/{id}.reparto (rules: owner-only field); every booking from
 * then on snapshots it. Fianzas already on the books keep their cut unless the
 * owner ticks them in the retroactive dialog that follows the save.
 *
 * When the org requires approval (config/asociados.requiereAprobacion) the
 * save becomes a propuesta: backend-nyx stores it, the socios get an email,
 * and the reparto plus the ticked fianzas apply once they approve.
 */
export function ClienteAsociados({ clientId }) {
  const { user, userData } = useUser();
  const { asociados, asociadosById, requiereAprobacion, propuestas } = useAsociados();
  const { clientsById } = useClients();
  const { movements } = useComisionLedger();
  const contratos = useClientContracts(clientId);
  const cliente = clientsById?.[clientId];
  const guardado = cliente?.reparto?.primaNeta || null;

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [retro, setRetro] = useState(null); // { candidatos, motivo, propuesta? }
  const [aviso, setAviso] = useState(null);
  const pendiente = propuestaPara(propuestas, { clienteId: clientId });

  const base = useMemo(() => rowsParaEditor(asociados, guardado), [asociados, guardado]);
  useEffect(() => { setRows(base); }, [base]);

  const nuevo = primaNetaDesdeRows(rows);
  const cambio = JSON.stringify(nuevo) !== JSON.stringify(primaNetaDesdeRows(base));
  const invalido = errorRows(rows);

  // Fianzas already booked under a different cut than the STORED reparto.
  // Derived from the ledger every render, so closing the retroactive dialog
  // without adjusting never hides anything: the count stays until the owner
  // restates them (or the socios approve a propuesta that carries them).
  const desfasadas = useMemo(
    () => candidatosReparto({
      movements,
      contratos,
      clientesById: { [clientId]: cliente || {} },
      asociadosById,
    }),
    [movements, contratos, clientId, cliente, asociadosById]
  );

  const revisarDesfasadas = () => {
    const motivo = `Reparto del cliente: ${describirReparto(guardado, asociadosById)}`;
    setError(null);
    setAviso(null);
    setRetro({ candidatos: desfasadas, motivo, propuesta: requiereAprobacion, reparto: guardado });
  };

  // Approval path: propose (with the fianzas the owner ticks). The correo to
  // the socios leaves from functions onRepartoPropuestaCreada as soon as the
  // propuesta exists; the pending banner below shows whether it went out.
  const enviarPropuesta = async (fianzasARestatear, motivo, reparto = nuevo) => {
    await proponerReparto({
      objetivo: { tipo: 'cliente', clienteId: clientId },
      reparto,
      fianzasARestatear,
      motivo,
    });
    setRows(base);
    setAviso('Propuesta registrada. El correo a los socios sale en unos segundos.');
  };

  const guardar = async () => {
    if (invalido) { setError(invalido); return; }
    setSaving(true);
    setError(null);
    setAviso(null);
    if (requiereAprobacion) {
      try {
        const descripcion = describirReparto(nuevo, asociadosById);
        const motivo = `Reparto del cliente: ${descripcion}`;
        const candidatos = candidatosReparto({
          movements,
          contratos,
          clientesById: { [clientId]: { ...(cliente || {}), reparto: nuevo ? { primaNeta: nuevo } : null } },
          asociadosById,
        });
        if (candidatos.length) setRetro({ candidatos, motivo, propuesta: true });
        else await enviarPropuesta([], motivo);
      } catch (e) {
        console.error('reparto propuesta failed', e);
        setError(e.message || 'No se pudo enviar la propuesta.');
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const reparto = nuevo
        ? {
            primaNeta: nuevo,
            otrosIngresos: otrosIngresosDesde(nuevo, asociadosById),
            updatedAt: Date.now(),
            updatedBy: user.uid,
          }
        : null;
      await updateDoc(doc(db, 'clientes', clientId), { reparto });
      const descripcion = describirReparto(nuevo, asociadosById);
      await logBitacora({
        action: 'UPDATE',
        collection: 'clientes',
        documentId: clientId,
        changes: cambiosReparto(guardado, nuevo),
        metadata: {
          clientId,
          clientName: cliente?.nombreEmpresa,
          description: `Reparto de comisión del cliente: ${descripcion}`,
        },
        user,
        userData,
      });
      const candidatos = candidatosReparto({
        movements,
        contratos,
        clientesById: { [clientId]: { ...(cliente || {}), reparto } },
        asociadosById,
      });
      if (candidatos.length) {
        setRetro({ candidatos, motivo: `Reparto del cliente: ${descripcion}` });
      }
    } catch (e) {
      console.error('reparto cliente save failed', e);
      setError('No se pudo guardar el reparto.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-base font-semibold text-azul-surelia">Reparto sobre prima neta</h3>
        <div className="flex gap-2">
          {cambio && (
            <Button plain onClick={() => { setRows(base); setError(null); }} disabled={saving}>
              Descartar
            </Button>
          )}
          <Button color="surelia" onClick={guardar} disabled={saving || !cambio}>
            {saving ? 'Guardando…' : requiereAprobacion ? 'Enviar a aprobación' : 'Guardar'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
      )}
      {aviso && (
        <div className="rounded-lg bg-azul-surelia/5 border border-azul-surelia/20 px-3 py-2 text-xs text-azul-surelia">{aviso}</div>
      )}

      <PropuestaRepartoPendiente propuesta={pendiente} />

      {!pendiente && desfasadas.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-amber-900">
            {desfasadas.length} fianza{desfasadas.length !== 1 ? 's' : ''} con reparto anterior
          </span>
          <button
            type="button"
            onClick={revisarDesfasadas}
            disabled={saving}
            className="text-xs font-medium text-azul-surelia hover:underline"
          >
            Revisar
          </button>
        </div>
      )}

      <RepartoEditor rows={rows} onChange={(r) => { setRows(r); setError(null); }} disabled={saving} />

      <RepartoRetroactivoDialog
        isOpen={Boolean(retro)}
        onClose={() => setRetro(null)}
        candidatos={retro?.candidatos}
        motivo={retro?.motivo}
        onEnviar={retro?.propuesta
          ? (cambios) => enviarPropuesta(cambios, retro.motivo, retro.reparto === undefined ? nuevo : retro.reparto)
          : undefined}
      />
    </div>
  );
}

export default ClienteAsociados;
